import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  X, Loader2, Sparkles, ShieldCheck, Euro, AlertTriangle, Trophy, FileText, Scale, BookOpen, StopCircle,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  ShieldCheck, Euro, AlertTriangle, Trophy, FileText, Scale, BookOpen, Sparkles,
};

const fmtInt = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtUSD = (n) => "$" + Number(n || 0).toFixed(4);

/**
 * Modal "Generar análisis Pando"
 * props: { open, onClose, lic, onGenerated(file_id) }
 */
export default function GenerateAIAnalysisModal({ open, onClose, lic, onGenerated }) {
  const [prompts, setPrompts] = useState([]);
  const [selected, setSelected] = useState("resumen_ejecutivo");
  const [customPrompt, setCustomPrompt] = useState("");
  const [includeDocs, setIncludeDocs] = useState(true);
  const [loading, setLoading] = useState(false);

  // Preflight (tokens + coste estimados + warnings)
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  // Live token counter (streaming)
  const [streaming, setStreaming] = useState(false);
  const [streamState, setStreamState] = useState(null); // { input_tokens, output_tokens, cost_usd, chars, startedAt }
  const [streamPreview, setStreamPreview] = useState("");
  const [thinkingText, setThinkingText] = useState("");
  const [showThinking, setShowThinking] = useState(true);
  const [mode, setMode] = useState("auto"); // "auto" | "single" | "batched"
  const [enableThinking, setEnableThinking] = useState(true);
  const [batchStatus, setBatchStatus] = useState(null); // { idx, total, name } (legacy: sólo último)
  const [parallelBatches, setParallelBatches] = useState({}); // {idx: {status: 'running'|'done'|'failed', name, chars, out_tokens}}
  const [mapPhase, setMapPhase] = useState(null); // { total, concurrency, failed }
  const [phase, setPhase] = useState(null); // "synthesis"
  const [resumableSessions, setResumableSessions] = useState([]);
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [synthesisOnly, setSynthesisOnly] = useState(false);
  const [synthesisModel, setSynthesisModel] = useState("claude-sonnet-4-6");
  const [overridePrompt, setOverridePrompt] = useState(false);
  // Detección de cuelgue: último timestamp en que llegó un token real (no keepalive)
  const [lastTokenAt, setLastTokenAt] = useState(null);
  const [secondsSinceToken, setSecondsSinceToken] = useState(0);
  const [stalledFromServer, setStalledFromServer] = useState(0); // del evento 'progress'
  // Librería de prompts de síntesis preferidos (globales + del usuario)
  const [synthPromptsLib, setSynthPromptsLib] = useState([]);
  const [showSynthLibManager, setShowSynthLibManager] = useState(false);
  const [newSynthPromptName, setNewSynthPromptName] = useState("");
  const [newSynthPromptText, setNewSynthPromptText] = useState("");
  // Live estimación tokens (debounced) para mostrar si activa prompt caching
  const [promptTokenInfo, setPromptTokenInfo] = useState(null); // {estimated_tokens, cacheable}
  const [promptTokenLoading, setPromptTokenLoading] = useState(false);
  // Modo Ráfaga: multi-selección + estado de ejecución
  const [burstMode, setBurstMode] = useState(false);
  const [burstSelected, setBurstSelected] = useState([]); // ids de prompts
  const [burstRunning, setBurstRunning] = useState(false);
  const [burstResults, setBurstResults] = useState(null);
  const previewRef = React.useRef(null);
  const thinkingRef = React.useRef(null);
  // AbortController para cancelar el fetch de streaming bajo demanda
  const abortRef = React.useRef(null);
  const readerRef = React.useRef(null);
  const [aborting, setAborting] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/licitaciones/ai-analysis/prompts`)
      .then(r => setPrompts(r.data || []))
      .catch(() => toast.error("Error cargando prompts"));
  }, [open]);

  // Cargar sesiones reanudables al abrir el modal
  useEffect(() => {
    if (!open || !lic?.id) return;
    axios.get(`${API}/licitaciones/${lic.id}/ai-analysis/sessions`)
      .then(r => setResumableSessions(r.data?.sessions || []))
      .catch(() => setResumableSessions([]));
  }, [open, lic?.id]);

  // Cargar librería de prompts de síntesis preferidos
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/licitaciones/ai-analysis/synthesis-prompts`)
      .then(r => setSynthPromptsLib(r.data?.prompts || []))
      .catch(() => setSynthPromptsLib([]));
  }, [open]);

  // Debounced live token estimation para mostrar barra de progreso hacia
  // el umbral de prompt caching (≥1024 tokens → 90% off en hits).
  useEffect(() => {
    const text = (newSynthPromptText || "").trim();
    if (!text) {
      setPromptTokenInfo(null);
      return;
    }
    if (text.length < 30) {
      // Estimación local barata para textos muy cortos (evita ruido)
      setPromptTokenInfo({ estimated_tokens: Math.round(text.length / 3.5), cacheable: false });
      return;
    }
    setPromptTokenLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${API}/licitaciones/ai-analysis/synthesis-prompts/estimate`,
          { text },
        );
        setPromptTokenInfo(r.data);
      } catch {
        setPromptTokenInfo({ estimated_tokens: Math.round(text.length / 3.5), cacheable: false });
      } finally {
        setPromptTokenLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [newSynthPromptText]);

  const saveSynthPrompt = async () => {
    const name = newSynthPromptName.trim();
    const prompt = newSynthPromptText.trim();
    if (!name || !prompt) return toast.error("Nombre y prompt son obligatorios");
    try {
      const r = await axios.post(`${API}/licitaciones/ai-analysis/synthesis-prompts`, {
        name, prompt, scope: "personal", icon: "Sparkles",
      });
      setSynthPromptsLib(prev => [r.data, ...prev]);
      setNewSynthPromptName("");
      setNewSynthPromptText("");
      toast.success(`Prompt "${r.data.name}" guardado`);
    } catch (e) {
      toast.error("Error guardando prompt: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteSynthPrompt = async (pid) => {
    if (!window.confirm("¿Borrar este prompt de tu librería?")) return;
    try {
      await axios.delete(`${API}/licitaciones/ai-analysis/synthesis-prompts/${pid}`);
      setSynthPromptsLib(prev => prev.filter(p => p.id !== pid));
      toast.success("Prompt borrado");
    } catch { toast.error("Error borrando"); }
  };

  const applySynthPrompt = (p) => {
    setSelected("custom");
    setCustomPrompt(p.prompt);
    setOverridePrompt(true);
    toast.success(`Prompt aplicado: ${p.name}`);
  };

  const toggleBurstPrompt = (pid) => {
    setBurstSelected(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const launchBurst = async () => {
    if (burstSelected.length < 2) return toast.error("Selecciona al menos 2 prompts");
    if (burstSelected.length > 6) return toast.error("Máximo 6 prompts por ráfaga");
    const selSession = resumableSessions.find(s => s.id === resumeSessionId);
    if (!selSession) return toast.error("No hay sesión seleccionada");
    setBurstRunning(true);
    setBurstResults(null);
    try {
      const r = await axios.post(`${API}/licitaciones/${lic.id}/ai-analysis/burst`, {
        session_id: resumeSessionId,
        prompt_ids: burstSelected,
        synthesis_model: synthesisModel,
        enable_thinking: enableThinking,
      }, { timeout: 600000 });
      setBurstResults(r.data);
      const succ = r.data.succeeded_count || 0;
      const cost = (r.data.total_cost_usd || 0).toFixed(3);
      toast.success(`🚀 Ráfaga completada: ${succ}/${burstSelected.length} síntesis · $${cost}`);
      // Refresca lista de archivos en el padre
      onGenerated && onGenerated(r.data.results?.[0]?.file_id);
    } catch (e) {
      toast.error("Error en ráfaga: " + (e.response?.data?.detail || e.message));
    } finally {
      setBurstRunning(false);
    }
  };

  // Contador "última actividad hace Xs" durante streaming
  useEffect(() => {
    if (!streaming || !lastTokenAt) return;
    const id = setInterval(() => {
      setSecondsSinceToken(Math.floor((Date.now() - lastTokenAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [streaming, lastTokenAt]);

  const discardSession = async (sid) => {
    try {
      await axios.post(`${API}/licitaciones/${lic.id}/ai-analysis/sessions/${sid}/discard`);
      setResumableSessions(prev => prev.filter(s => s.id !== sid));
      if (resumeSessionId === sid) setResumeSessionId(null);
      toast.success("Sesión descartada");
    } catch { toast.error("Error descartando"); }
  };

  useEffect(() => {
    if (!open || !lic?.id) return;
    setPreflightLoading(true);
    axios.get(`${API}/licitaciones/${lic.id}/ai-preflight`, { params: { include_docs: includeDocs } })
      .then(r => setPreflight(r.data))
      .catch(() => setPreflight(null))
      .finally(() => setPreflightLoading(false));
  }, [open, lic?.id, includeDocs]);

  if (!open) return null;

  const run = async () => {
    if (selected === "custom" && !customPrompt.trim()) {
      return toast.error("Escribe tu consulta");
    }
    setLoading(true);
    setStreaming(true);
    setAborting(false);
    setStreamState({ input_tokens: 0, output_tokens: 0, cost_usd: 0, chars: 0, startedAt: Date.now() });
    setStreamPreview("");
    setThinkingText("");
    setBatchStatus(null);
    setParallelBatches({});
    setMapPhase(null);
    setPhase(null);
    setLastTokenAt(Date.now());
    setSecondsSinceToken(0);
    setStalledFromServer(0);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem("valoriza_token") || "";
      const resp = await fetch(`${API}/licitaciones/${lic.id}/ai-analysis/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt_key: selected,
          custom_prompt: customPrompt,
          include_docs: includeDocs,
          mode,
          enable_thinking: enableThinking,
          synthesis_model: synthesisModel,
          ...(resumeSessionId ? { resume_session_id: resumeSessionId } : {}),
          ...(synthesisOnly ? { synthesis_only: true } : {}),
          ...(overridePrompt ? { override_prompt: true } : {}),
        }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        const err = await resp.text();
        throw new Error(err || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = "";
      let doneFile = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const ev of events) {
          const line = ev.split("\n").find(l => l.startsWith("data: "));
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }

          if (payload.type === "start") {
            setStreamState(s => ({ ...s, input_tokens: payload.input_tokens_est || 0, mode: payload.mode, batches_total: payload.batches_total }));
          } else if (payload.type === "input_counted") {
            setStreamState(s => ({
              ...s,
              input_tokens: payload.input_tokens,
              cost_usd: payload.cost_usd_so_far,
            }));
          } else if (payload.type === "thinking") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            setThinkingText(prev => {
              const next = prev + (payload.delta || "");
              setTimeout(() => {
                if (thinkingRef.current) thinkingRef.current.scrollTop = thinkingRef.current.scrollHeight;
              }, 0);
              return next;
            });
          } else if (payload.type === "map_phase_start") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            setMapPhase({ total: payload.total, concurrency: payload.concurrency, failed: 0 });
            setPhase(`Extracción paralela (${payload.concurrency} en paralelo · ${payload.total} lotes)`);
          } else if (payload.type === "map_phase_done") {
            setMapPhase(prev => ({ ...(prev || {}), total: payload.total, failed: payload.failed }));
            setPhase("Extracción completada · iniciando síntesis…");
          } else if (payload.type === "batch_start") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            setBatchStatus({ idx: payload.idx, total: payload.total, name: payload.name, chars: payload.chars });
            setParallelBatches(prev => ({
              ...prev,
              [payload.idx]: { status: "running", name: payload.name, chars: payload.chars, out_tokens: 0 },
            }));
          } else if (payload.type === "batch_done") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            setParallelBatches(prev => ({
              ...prev,
              [payload.idx]: { ...(prev[payload.idx] || {}), status: "done", out_tokens: payload.output_tokens, cost_usd: payload.cost_usd },
            }));
          } else if (payload.type === "batch_retry") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            toast.warning(`Reintentando lote ${payload.idx} (intento ${payload.attempt}/3) — ${payload.reason}`);
          } else if (payload.type === "batch_failed") {
            setParallelBatches(prev => ({
              ...prev,
              [payload.idx]: { ...(prev[payload.idx] || {}), status: "failed" },
            }));
            toast.error(`Lote ${payload.idx}/${payload.total} falló tras 3 intentos. Continuamos con el resto.`);
          } else if (payload.type === "synth_retry") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            toast.warning(`Reintentando síntesis (intento ${payload.attempt}/3) — ${payload.reason}`);
          } else if (payload.type === "synth_failed") {
            // No es un error fatal: el backend preserva contenido parcial.
            if (payload.salvageable) {
              toast.warning(`Síntesis truncada (${payload.reason}). Se preservaron ${payload.partial_chars.toLocaleString("es-ES")} chars parciales. Puedes reanudar con "Sólo síntesis".`, { duration: 12000 });
            } else {
              toast.error(`Síntesis falló (${payload.reason}). Los extractos siguen disponibles — reanuda con "Sólo síntesis" para reintentar.`, { duration: 12000 });
            }
          } else if (payload.type === "progress") {
            // Heartbeat enriquecido del backend: nos indica cuánto lleva sin actividad real
            setStalledFromServer(payload.stalled_s || 0);
          } else if (payload.type === "phase") {
            if (payload.name === "synthesis") {
              setPhase("Síntesis final");
              setBatchStatus(null);
            }
          } else if (payload.type === "token") {
            setLastTokenAt(Date.now());
            setStalledFromServer(0);
            // Si el token viene de un lote en paralelo (map phase), NO lo acumulamos
            // en el preview principal — sólo contamos tokens del lote. Si NO trae
            // batch_idx, es síntesis y va al preview final.
            if (payload.batch_idx) {
              setParallelBatches(prev => {
                const b = prev[payload.batch_idx] || { status: "running", out_tokens: 0 };
                return {
                  ...prev,
                  [payload.batch_idx]: {
                    ...b,
                    out_tokens: payload.output_tokens_est || (b.out_tokens || 0),
                  },
                };
              });
              setStreamState(s => ({
                ...s,
                cost_usd: payload.cost_usd,
              }));
            } else {
              setStreamPreview(prev => {
                const next = prev + (payload.delta || "");
                setTimeout(() => {
                  if (previewRef.current) previewRef.current.scrollTop = previewRef.current.scrollHeight;
                }, 0);
                return next;
              });
              setStreamState(s => ({
                ...s,
                output_tokens: payload.output_tokens_est,
                cost_usd: payload.cost_usd,
                chars: (s?.chars || 0) + (payload.delta?.length || 0),
              }));
            }
          } else if (payload.type === "done") {
            doneFile = payload;
            setStreamState(s => ({
              ...s,
              input_tokens: payload.input_tokens,
              output_tokens: payload.output_tokens,
              cost_usd: payload.cost_usd,
              chars: payload.length,
            }));
          } else if (payload.type === "error") {
            throw new Error(payload.detail || "Error desconocido Pando");
          }
        }
      }

      if (doneFile) {
        toast.success(`✓ ${doneFile.length.toLocaleString("es-ES")} chars · ${doneFile.output_tokens.toLocaleString("es-ES")} tok · $${doneFile.cost_usd}`);
        setResumeSessionId(null);
        setSynthesisOnly(false);
        setOverridePrompt(false);
        // Refresca lista resumable (la sesión actual debería desaparecer)
        try {
          const r = await axios.get(`${API}/licitaciones/${lic.id}/ai-analysis/sessions`);
          setResumableSessions(r.data?.sessions || []);
        } catch {}
        onGenerated && onGenerated(doneFile.file_id);
        setTimeout(() => onClose(), 900);
      }
    } catch (e) {
      if (e.name === "AbortError" || controller.signal.aborted) {
        // Cancelación voluntaria del usuario: el backend persiste el parcial
        // como draft con `is_draft=true, aborted=true`. Refrescamos la lista de
        // archivos para que el draft aparezca inmediatamente.
        toast.success("Generación detenida. El parcial recibido se ha guardado como borrador.");
        // Refresca lista resumable (puede aparecer la nueva sesión interrumpida)
        try {
          const r = await axios.get(`${API}/licitaciones/${lic.id}/ai-analysis/sessions`);
          setResumableSessions(r.data?.sessions || []);
        } catch {}
        onGenerated && onGenerated(null);
        setStreaming(false);
      } else {
        toast.error("Error Pando (stream): " + (e.message || "desconocido"));
        setStreaming(false);
      }
    } finally {
      setLoading(false);
      setAborting(false);
      abortRef.current = null;
      readerRef.current = null;
    }
  };

  const stop = () => {
    // Defensa: aunque abortRef esté null, igualmente forzamos UI a "detenido".
    setAborting(true);
    // 1) Cancelar el reader (cierra el stream del lado cliente inmediatamente).
    try { readerRef.current?.cancel("user-abort"); } catch (_) {}
    // 2) Abortar el fetch (cierra la conexión TCP).
    try { abortRef.current?.abort(); } catch (_) {}
    // 3) Si tras 2s el run() loop no ha cerrado por algún proxy intermedio
    //    que tarda en propagar el close, forzamos la UI a "stopped" igualmente.
    //    Los lotes ya extraídos quedan persistidos en backend; podrás reanudar.
    setTimeout(() => {
      setStreaming(false);
      setLoading(false);
      setAborting(false);
      abortRef.current = null;
      readerRef.current = null;
    }, 1500);
  };

  const cur = prompts.find(p => p.key === selected);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-gen-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-5 py-3 z-10">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-purple-600"/>
            <h3 className="text-sm font-bold">Generar análisis Pando</h3>
            <span className="bg-purple-100 text-purple-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">Claude Sonnet 4.6</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-500">
            Selecciona el tipo de análisis. Cada preset incluye un <strong>prompt optimizado</strong> y el contexto (título, objeto, presupuesto, documentos indexados). El resultado se guarda como un archivo Pando descargable en PDF con portada Valoriza y gráficos.
          </p>

          {/* Preset grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {prompts.map(p => {
              const Icon = ICON_MAP[p.icon] || Sparkles;
              const active = selected === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => setSelected(p.key)}
                  data-testid={`preset-${p.key}`}
                  className={`rounded-xl border-2 p-3 text-left transition-all ${
                    active ? "border-[#007934] bg-[#007934]/5" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`rounded-lg p-1.5 ${active ? "bg-[#007934] text-white" : "bg-gray-100 text-gray-600"}`}>
                      <Icon size={14}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{p.title}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">{p.description}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom prompt input */}
          {selected === "custom" && (
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 block">Tu consulta *</label>
              <textarea
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                rows={5}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs resize-y focus:outline-none focus:border-[#007934]"
                placeholder="Ej: Analiza si el pliego permite subcontratar el servicio de limpieza de cristales en una tercera empresa especializada."
                data-testid="custom-prompt-input"
              />
            </div>
          )}

          {/* Toggle include docs */}
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={includeDocs} onChange={e => setIncludeDocs(e.target.checked)} data-testid="include-docs"/>
            Incluir documentos indexados de la licitación como contexto ({(lic.archivos || []).filter(f => !f.generated_ai && f.categoria !== "Análisis Pando").length} disponibles)
          </label>

          {/* Sesiones reanudables / reciclables */}
          {resumableSessions.length > 0 && (() => {
            const interrupted = resumableSessions.filter(s => s.status === "interrupted");
            const completed = resumableSessions.filter(s => s.status === "completed");
            const selSession = resumableSessions.find(s => s.id === resumeSessionId);
            const resumeCost = selSession ? (
              synthesisModel === "claude-opus-4-8"
                ? selSession.resume_cost_opus_usd
                : selSession.resume_cost_sonnet_usd
            ) : 0;
            return (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="resume-sessions-panel">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-[11px] font-bold text-amber-900 flex items-center gap-1.5">
                    Sesiones Pando previas ({resumableSessions.length})
                  </div>
                  <div className="text-[10px] text-amber-700">
                    ⏸ Reanudar interrumpido · ♻ Reciclar completado con nuevo prompt
                  </div>
                </div>

                {/* INTERRUMPIDAS */}
                {interrupted.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] font-bold text-amber-800 uppercase tracking-wide">⏸ Interrumpidas (reanudables)</div>
                    {interrupted.map(s => {
                      const isReady = s.is_complete_for_resume;
                      const sel = resumeSessionId === s.id;
                      const sCost = synthesisModel === "claude-opus-4-8" ? s.resume_cost_opus_usd : s.resume_cost_sonnet_usd;
                      return (
                        <div
                          key={s.id}
                          data-testid={`resume-session-${s.id}`}
                          className={`bg-white rounded p-2 border ${sel ? "border-amber-500 ring-1 ring-amber-300" : "border-amber-200"} text-[11px] flex items-center gap-2`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-800 truncate">
                              {s.prompt_key} · {s.extracts_done}/{s.batches_total} lotes
                            </div>
                            <div className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
                              <span>{new Date(s.started_at).toLocaleString("es-ES", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })}</span>
                              <span>· {s.started_by}</span>
                              <span className="text-emerald-700">· invertido ${(s.invested_cost_usd ?? 0).toFixed(3)}</span>
                              <span className="text-purple-700 font-semibold">· reanudar ~${(sCost ?? 0).toFixed(3)}</span>
                            </div>
                          </div>
                          {isReady ? (
                            <button
                              onClick={() => { setResumeSessionId(sel ? null : s.id); setSynthesisOnly(false); }}
                              data-testid={`resume-session-pick-${s.id}`}
                              className={`text-[10px] font-bold px-2 py-1 rounded shrink-0 ${
                                sel ? "bg-amber-600 text-white" : "bg-amber-100 hover:bg-amber-200 text-amber-800"
                              }`}>
                              {sel ? "✓ Reanudar al lanzar" : "Reanudar"}
                            </button>
                          ) : (
                            <span className="text-[10px] text-gray-400 italic shrink-0">incompleto</span>
                          )}
                          <button
                            onClick={() => discardSession(s.id)}
                            data-testid={`resume-session-discard-${s.id}`}
                            title="Descartar"
                            className="text-rose-500 hover:text-rose-700 text-[10px] px-1.5">
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* COMPLETADAS (reciclables) */}
                {completed.length > 0 && (
                  <div className="space-y-1.5 pt-1 border-t border-amber-200">
                    <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide">♻ Completadas (reciclar con nuevo prompt — ahorra ~90%)</div>
                    {completed.map(s => {
                      const sel = resumeSessionId === s.id;
                      const sCost = synthesisModel === "claude-opus-4-8" ? s.resume_cost_opus_usd : s.resume_cost_sonnet_usd;
                      return (
                        <div
                          key={s.id}
                          data-testid={`recycle-session-${s.id}`}
                          className={`bg-white rounded p-2 border ${sel ? "border-emerald-500 ring-1 ring-emerald-300" : "border-emerald-200"} text-[11px] flex items-center gap-2`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-800 truncate">
                              {s.prompt_key} · {s.batches_done || s.extracts_done}/{s.batches_total} lotes · ✓
                            </div>
                            <div className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
                              <span>{new Date(s.finished_at || s.started_at).toLocaleString("es-ES", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })}</span>
                              <span>· {s.started_by}</span>
                              <span className="text-emerald-700">· invertido ${(s.invested_cost_usd ?? 0).toFixed(3)}</span>
                              <span className="text-purple-700 font-semibold">· reciclar ~${(sCost ?? 0).toFixed(3)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              if (sel) { setResumeSessionId(null); setSynthesisOnly(false); setOverridePrompt(false); }
                              else { setResumeSessionId(s.id); setSynthesisOnly(true); }
                            }}
                            data-testid={`recycle-session-pick-${s.id}`}
                            className={`text-[10px] font-bold px-2 py-1 rounded shrink-0 ${
                              sel ? "bg-emerald-600 text-white" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
                            }`}>
                            {sel ? "✓ Reciclar al lanzar" : "Reciclar"}
                          </button>
                          <button
                            onClick={() => discardSession(s.id)}
                            data-testid={`recycle-session-discard-${s.id}`}
                            title="Descartar"
                            className="text-rose-500 hover:text-rose-700 text-[10px] px-1.5">
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Banda informativa cuando hay una seleccionada */}
                {resumeSessionId && selSession && (
                  <div className={`text-[10px] rounded px-2 py-1.5 space-y-1 ${synthesisOnly ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-800"}`}>
                    {synthesisOnly ? (
                      <>
                        <div className="font-bold">♻ Modo &quot;Sólo síntesis&quot; activo</div>
                        <div>Se reutilizarán los <b>{selSession.extracts_done} extractos</b> ya procesados. Sólo se relanzará la síntesis final con el prompt que elijas arriba.</div>
                        <label className="flex items-center gap-1.5 cursor-pointer mt-1">
                          <input type="checkbox" checked={overridePrompt} onChange={e => setOverridePrompt(e.target.checked)} data-testid="override-prompt"/>
                          <span><b>Cambiar prompt</b> respecto al original ({selSession.prompt_key})</span>
                        </label>

                        {/* Librería de prompts de síntesis preferidos */}
                        {synthPromptsLib.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-emerald-300 space-y-1.5" data-testid="synth-prompts-library">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="text-[10px] uppercase font-bold tracking-wide text-emerald-900">
                                ⚡ Prompts preferidos {burstMode ? "(selecciona varios para Ráfaga)" : "(click para aplicar)"}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => { setBurstMode(b => !b); setBurstSelected([]); setBurstResults(null); }}
                                  data-testid="burst-mode-toggle"
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded transition-colors ${
                                    burstMode ? "bg-purple-600 text-white" : "bg-purple-100 text-purple-800 hover:bg-purple-200"
                                  }`}>
                                  🚀 Modo Ráfaga {burstMode ? "ON" : "OFF"}
                                </button>
                                <button
                                  onClick={() => setShowSynthLibManager(s => !s)}
                                  data-testid="synth-lib-toggle-manager"
                                  className="text-[10px] text-emerald-700 hover:text-emerald-900 underline">
                                  {showSynthLibManager ? "− cerrar" : "+ añadir mío"}
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {synthPromptsLib.map(p => {
                                const inBurst = burstSelected.includes(p.id);
                                return (
                                  <button
                                    key={p.id}
                                    onClick={() => burstMode ? toggleBurstPrompt(p.id) : applySynthPrompt(p)}
                                    data-testid={`synth-preset-${p.id}`}
                                    title={p.prompt.slice(0, 200) + (p.prompt.length > 200 ? "…" : "")}
                                    className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                                      burstMode
                                        ? (inBurst ? "bg-purple-600 border-purple-700 text-white" : "bg-white border-purple-300 hover:bg-purple-50 text-purple-900")
                                        : (p.scope === "personal" ? "bg-white border-emerald-300 hover:bg-emerald-100 text-emerald-900"
                                                                   : "bg-emerald-100 border-emerald-400 hover:bg-emerald-200 text-emerald-900")
                                    }`}>
                                    {burstMode && (inBurst ? "✓ " : "○ ")}
                                    {!burstMode && p.scope === "personal" ? "👤 " : ""}{p.name}
                                    {!burstMode && p.scope === "personal" && (
                                      <span
                                        onClick={e => { e.stopPropagation(); deleteSynthPrompt(p.id); }}
                                        className="ml-1 text-rose-500 hover:text-rose-700 cursor-pointer"
                                        data-testid={`synth-preset-delete-${p.id}`}>×</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            {burstMode && (() => {
                              const n = burstSelected.length;
                              const baseCost = synthesisModel === "claude-opus-4-8"
                                ? (selSession?.resume_cost_opus_usd ?? 0)
                                : (selSession?.resume_cost_sonnet_usd ?? 0);
                              const estTotal = n * baseCost;
                              return (
                                <div className="bg-purple-50 border border-purple-300 rounded p-2 space-y-1.5" data-testid="burst-panel">
                                  <div className="text-[10px] text-purple-900">
                                    <b>{n}</b> síntesis seleccionadas · coste estimado total <b>~${estTotal.toFixed(3)}</b> ({n}×${baseCost.toFixed(3)})
                                  </div>
                                  <button
                                    onClick={launchBurst}
                                    disabled={n < 2 || burstRunning}
                                    data-testid="burst-launch"
                                    className="w-full text-[11px] font-bold bg-purple-600 text-white px-3 py-1.5 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {burstRunning ? (
                                      <span className="inline-flex items-center gap-1.5">
                                        <Loader2 className="animate-spin" size={11}/> Ejecutando ráfaga ({n} en paralelo)…
                                      </span>
                                    ) : (
                                      `🚀 Lanzar ráfaga (${n} síntesis simultáneas)`
                                    )}
                                  </button>
                                  {burstResults && (
                                    <div className="bg-white rounded p-1.5 space-y-1 text-[10px]" data-testid="burst-results">
                                      <div className="font-bold text-purple-900">
                                        Resultados: {burstResults.succeeded_count} OK / {burstResults.failed_count} KO · Total ${burstResults.total_cost_usd}
                                      </div>
                                      {burstResults.results.map(r => (
                                        <div key={r.prompt_id} className={`flex items-center gap-1.5 ${r.file_id ? "text-emerald-800" : "text-rose-700"}`}>
                                          <span>{r.file_id ? "✓" : "✗"}</span>
                                          <span className="flex-1 truncate">{r.prompt_name}</span>
                                          {r.file_id ? (
                                            <span className="text-gray-500">{(r.length||0).toLocaleString("es-ES")} chars · ${r.cost_usd}</span>
                                          ) : (
                                            <span className="text-rose-600 truncate" title={r.error}>{(r.error||"").slice(0,60)}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {showSynthLibManager && (
                              <div className="bg-white rounded p-2 border border-emerald-200 space-y-1.5" data-testid="synth-prompt-creator">
                                <input
                                  type="text"
                                  value={newSynthPromptName}
                                  onChange={e => setNewSynthPromptName(e.target.value)}
                                  placeholder="Nombre corto (ej. Riesgos urbanos)"
                                  data-testid="synth-prompt-name"
                                  className="w-full text-[11px] border border-gray-200 rounded px-2 py-1"
                                />
                                <textarea
                                  value={newSynthPromptText}
                                  onChange={e => setNewSynthPromptText(e.target.value)}
                                  placeholder="Instrucciones para la síntesis (markdown ok)…"
                                  data-testid="synth-prompt-text"
                                  rows={3}
                                  className="w-full text-[11px] border border-gray-200 rounded px-2 py-1 font-mono resize-y"
                                />
                                {/* Live token counter + barra hacia 1024 (umbral cache) */}
                                {newSynthPromptText && (() => {
                                  const tk = promptTokenInfo?.estimated_tokens ?? 0;
                                  const pct = Math.min(100, (tk / 1024) * 100);
                                  const ok = tk >= 1024;
                                  return (
                                    <div className="space-y-1" data-testid="prompt-token-meter">
                                      <div className="flex items-center justify-between text-[10px]">
                                        <span className={`font-bold ${ok ? "text-emerald-700" : "text-amber-700"}`}>
                                          {promptTokenLoading ? "Calculando…" : `${tk.toLocaleString("es-ES")} tokens`}
                                          {ok && <span className="ml-1 bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-bold">✓ CACHEABLE</span>}
                                        </span>
                                        <span className="text-gray-500">
                                          {ok
                                            ? "90% off en hits"
                                            : `${(1024 - tk).toLocaleString("es-ES")} tokens para cache`}
                                        </span>
                                      </div>
                                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full transition-all ${ok ? "bg-emerald-500" : "bg-amber-400"}`}
                                          style={{ width: `${pct}%` }}
                                        />
                                      </div>
                                      {!ok && tk > 100 && (
                                        <p className="text-[9px] text-gray-500 italic">
                                          Tip: añade metodología, checklists o ejemplos. Anthropic cachea bloques ≥1024 tokens.
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}
                                <button
                                  onClick={saveSynthPrompt}
                                  data-testid="synth-prompt-save"
                                  className="text-[10px] font-bold bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700">
                                  Guardar en mi librería
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div>⚡ Se saltarán los lotes ya completados y se ejecutará sólo la <b>síntesis final</b>. Coste estimado: <b>~${(resumeCost ?? 0).toFixed(3)}</b> con {synthesisModel === "claude-opus-4-8" ? "Opus 4.8" : "Sonnet 4.6"}.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Modo + razonamiento + modelo de síntesis */}
          <div className="bg-purple-50/60 border border-purple-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold text-purple-700 tracking-wide">Modo</span>
                <div className="inline-flex rounded-lg overflow-hidden border border-purple-300 bg-white" data-testid="ai-mode-selector">
                  {[
                    { v: "auto", label: "Auto" },
                    { v: "single", label: "1 llamada" },
                    { v: "batched", label: "Por lotes (map-reduce)" },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setMode(opt.v)}
                      data-testid={`ai-mode-${opt.v}`}
                      className={`text-[11px] px-2.5 py-1 font-semibold transition-colors ${
                        mode === opt.v ? "bg-purple-600 text-white" : "text-purple-700 hover:bg-purple-50"
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-purple-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableThinking}
                  onChange={e => setEnableThinking(e.target.checked)}
                  data-testid="enable-thinking"
                  className="accent-purple-600"
                />
                <strong>Mostrar razonamiento</strong> (extended thinking)
              </label>
            </div>

            {/* Selector de modelo de síntesis */}
            <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-purple-200">
              <span className="text-[10px] uppercase font-bold text-purple-700 tracking-wide">Modelo síntesis</span>
              <div className="inline-flex rounded-lg overflow-hidden border border-purple-300 bg-white" data-testid="synth-model-selector">
                {[
                  { v: "claude-sonnet-4-6", label: "Sonnet 4.6", sub: "$3 IN · $15 OUT" },
                  { v: "claude-opus-4-8",   label: "Opus 4.8",   sub: "$5 IN · $25 OUT" },
                ].map(opt => (
                  <button
                    key={opt.v}
                    onClick={() => setSynthesisModel(opt.v)}
                    data-testid={`synth-model-${opt.v}`}
                    title={opt.sub}
                    className={`text-[11px] px-2.5 py-1 font-semibold transition-colors ${
                      synthesisModel === opt.v ? "bg-purple-600 text-white" : "text-purple-700 hover:bg-purple-50"
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="text-[10px] text-purple-600">
                Los lotes (extracción) siempre van con Sonnet (más barato). Sólo la síntesis final usa el modelo seleccionado.
              </span>
            </div>

            <p className="text-[10px] text-purple-700/80">
              <strong>Auto</strong> elige batched si la documentación supera 700K chars.
              <strong> Por lotes</strong> divide los documentos en grupos de ≤180K chars, los analiza por separado y luego sintetiza. Más fiable con pliegos largos.
              <strong> Razonamiento</strong> muestra lo que Claude está pensando antes de responder.
            </p>
          </div>

          {/* Preflight: qué se enviará a Claude */}
          {preflight && (
            <div data-testid="ai-preflight" className="bg-purple-50/40 border border-purple-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] font-bold text-purple-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles size={11} /> Preflight Pando · Claude Sonnet 4.6
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="font-semibold text-gray-700">
                    {fmtInt(preflight.total_chars_used)} / {fmtInt(preflight.total_char_budget)} chars
                    <span className="text-gray-400"> ({preflight.pct_budget_used}%)</span>
                  </span>
                  <span className="font-semibold text-gray-700">
                    ~{fmtInt(preflight.est_tokens_in)} tok IN + {fmtInt(preflight.est_tokens_out)} tok OUT
                  </span>
                  <span className="font-bold text-purple-800">{fmtUSD(preflight.est_cost_usd)}</span>
                </div>
              </div>
              {/* Barra de uso de presupuesto */}
              <div className="h-1.5 bg-white rounded overflow-hidden border border-purple-200">
                <div
                  className={`h-full ${preflight.pct_budget_used >= 90 ? "bg-red-500" : preflight.pct_budget_used >= 60 ? "bg-amber-500" : "bg-[#007934]"}`}
                  style={{ width: `${Math.min(100, preflight.pct_budget_used)}%` }}
                />
              </div>
              {/* Listado de archivos con indicador per-file */}
              {includeDocs && (preflight.files || []).length > 0 && (
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {preflight.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 text-[11px] text-gray-700">
                      <span className="flex-1 truncate">{f.nombre}</span>
                      {f.pages_count ? <span className="text-gray-400">{f.pages_count} pág</span> : null}
                      <span className={`font-semibold ${f.chars_total === 0 ? "text-amber-700" : f.truncated_per_file ? "text-red-700" : "text-green-700"}`}>
                        {f.chars_total === 0 ? "sin texto" : `${(f.chars_used/1000).toFixed(1)}K / ${(f.chars_total/1000).toFixed(1)}K`}
                      </span>
                      {f.truncated_per_file && <span className="text-[9px] bg-red-100 text-red-700 px-1 rounded border border-red-200">TRUNC</span>}
                    </div>
                  ))}
                </div>
              )}
              {/* Warnings */}
              {(preflight.warnings || []).length > 0 && (
                <div className="pt-1 border-t border-purple-200 space-y-1">
                  {preflight.warnings.map((w, i) => (
                    <div key={i} className={`text-[10px] flex items-start gap-1 ${w.severity === "high" ? "text-red-700" : "text-amber-700"}`}>
                      <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
                      <span>{w.msg}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {preflightLoading && !preflight && (
            <div className="bg-gray-50 rounded-lg p-3 text-[11px] text-gray-400 flex items-center gap-2">
              <Loader2 size={11} className="animate-spin" /> Calculando preflight…
            </div>
          )}

          {/* Summary + submit */}
          <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="text-[11px] text-gray-600">
              <strong>Licitación:</strong> {lic.titulo?.substring(0, 70)}{lic.titulo?.length > 70 ? "…" : ""}
              <br/>
              <strong>Análisis:</strong> {cur?.title || "—"}
            </div>
            {streaming ? (
              <button
                onClick={stop}
                disabled={aborting}
                data-testid="stop-btn"
                className="bg-red-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-red-700 disabled:opacity-60"
              >
                {aborting ? <><Loader2 className="animate-spin" size={14}/> Deteniendo…</> : <><StopCircle size={14}/> Detener</>}
              </button>
            ) : (
              <button
                onClick={run}
                disabled={loading}
                data-testid="generate-btn"
                className="bg-[#007934] text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#006528] disabled:opacity-50"
              >
                {loading ? <><Loader2 className="animate-spin" size={14}/> Generando…</> : <><Sparkles size={14}/> Generar</>}
              </button>
            )}
          </div>

          {loading && !streaming && (
            <div className="text-[11px] text-gray-400 text-center pt-2">
              El análisis puede tardar entre 30 y 90 segundos. No cierres esta ventana.
            </div>
          )}
          {streaming && (
            <div className="text-[11px] text-gray-400 text-center pt-2">
              Pulsa <strong className="text-red-600">Detener</strong> para cancelar la generación. El parcial recibido se guardará como borrador.
            </div>
          )}

          {/* Live counter + streaming preview */}
          {streaming && streamState && (
            <div data-testid="ai-live-counter" className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-lg p-3 space-y-2 mt-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"/>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-600"/>
                  </span>
                  Live · Claude Sonnet 4.6 {phase && <span className="ml-1 text-purple-600 normal-case font-bold">· {phase}</span>}
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-gray-700"><strong className="text-gray-900">{(streamState.input_tokens || 0).toLocaleString("es-ES")}</strong> tok IN</span>
                  <span className="text-gray-400">+</span>
                  <span className="text-gray-700"><strong className="text-purple-700">{(streamState.output_tokens || 0).toLocaleString("es-ES")}</strong> tok OUT</span>
                  <span className="text-gray-400">·</span>
                  <span className="font-bold text-purple-800">${Number(streamState.cost_usd || 0).toFixed(4)}</span>
                  <span className="text-gray-400">·</span>
                  <span className="text-gray-500">{Math.round((Date.now() - streamState.startedAt) / 1000)}s</span>
                  <button
                    onClick={stop}
                    disabled={aborting}
                    data-testid="stop-btn-inline"
                    title="Detener generación"
                    className="ml-2 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1 hover:bg-red-700 disabled:opacity-50"
                  >
                    {aborting ? <Loader2 className="animate-spin" size={10}/> : <StopCircle size={10}/>} {aborting ? "Deteniendo" : "Detener"}
                  </button>
                </div>
              </div>
              {/* Map phase: parallel batches grid */}
              {mapPhase && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2" data-testid="ai-map-phase">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-bold text-amber-900">
                      Extracción en paralelo · {mapPhase.concurrency} workers · {mapPhase.total} lotes
                    </div>
                    {(() => {
                      const entries = Object.values(parallelBatches);
                      const done = entries.filter(b => b.status === "done").length;
                      const failed = entries.filter(b => b.status === "failed").length;
                      const running = entries.filter(b => b.status === "running").length;
                      return (
                        <div className="text-[10px] text-amber-800">
                          <span className="font-bold text-emerald-700">{done}</span> hechos ·{" "}
                          <span className="font-bold text-amber-700">{running}</span> en curso
                          {failed > 0 && <> · <span className="font-bold text-rose-700">{failed}</span> fallidos</>}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-5 gap-1" data-testid="ai-batches-grid">
                    {Array.from({ length: mapPhase.total }, (_, i) => i + 1).map(idx => {
                      const b = parallelBatches[idx];
                      const st = b?.status || "pending";
                      const color =
                        st === "done" ? "bg-emerald-500 text-white border-emerald-600"
                        : st === "failed" ? "bg-rose-500 text-white border-rose-600"
                        : st === "running" ? "bg-amber-300 text-amber-900 border-amber-400 animate-pulse"
                        : "bg-gray-100 text-gray-500 border-gray-300";
                      return (
                        <div
                          key={idx}
                          data-testid={`ai-batch-${idx}-${st}`}
                          title={b?.name || `Lote ${idx}`}
                          className={`text-[10px] font-bold rounded border px-1 py-0.5 text-center truncate ${color}`}
                        >
                          {idx}
                          {b?.out_tokens ? <span className="ml-1 opacity-80">·{b.out_tokens}t</span> : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Fallback: single batch progress (legacy single-mode) */}
              {!mapPhase && batchStatus && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-800" data-testid="ai-batch-status">
                  <div className="font-bold">Procesando lote {batchStatus.idx}/{batchStatus.total}</div>
                  <div className="text-amber-700 truncate" title={batchStatus.name}>{batchStatus.name}</div>
                  <div className="text-amber-600">{(batchStatus.chars || 0).toLocaleString("es-ES")} chars en este lote</div>
                </div>
              )}

              {/* Indicador de salud del stream */}
              {(() => {
                const stalled = Math.max(secondsSinceToken, stalledFromServer);
                if (stalled < 20) return null;
                const isCritical = stalled >= 120;
                const isWarning = stalled >= 60;
                return (
                  <div
                    data-testid="ai-stream-health"
                    className={`rounded p-2 text-[10px] flex items-start gap-2 ${
                      isCritical ? "bg-rose-50 border border-rose-300 text-rose-800"
                      : isWarning ? "bg-amber-50 border border-amber-300 text-amber-800"
                      : "bg-sky-50 border border-sky-200 text-sky-800"
                    }`}>
                    <span className="text-base leading-none">{isCritical ? "🔴" : isWarning ? "🟠" : "🔵"}</span>
                    <div className="flex-1">
                      <div className="font-bold">
                        {isCritical ? `Sin progreso desde hace ${stalled}s — posible cuelgue`
                         : isWarning ? `Sin tokens desde hace ${stalled}s — Claude está pensando o esperando rate-limit`
                         : `Esperando respuesta de Claude (${stalled}s)…`}
                      </div>
                      {isCritical && (
                        <div className="mt-1 text-[10px]">
                          La conexión SSE sigue viva (heartbeat OK) pero Claude no produce salida.
                          Causas probables: <b>rate limit Anthropic</b>, <b>pliego muy largo</b> o <b>thinking budget agotado</b>.
                          Pulsa <b>Detener</b> — los lotes ya completados se guardan y podrás <b>reanudar</b> en cuanto quieras.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Thinking / Razonamiento panel */}
              {enableThinking && thinkingText && (
                <div className="bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="ai-thinking-panel">
                  <button
                    onClick={() => setShowThinking(s => !s)}
                    data-testid="ai-thinking-toggle"
                    className="w-full text-left text-[10px] uppercase font-bold text-indigo-700 tracking-wide flex items-center justify-between mb-1">
                    <span>🧠 Razonamiento ({thinkingText.length.toLocaleString("es-ES")} chars)</span>
                    <span className="text-indigo-500">{showThinking ? "▼ ocultar" : "▶ mostrar"}</span>
                  </button>
                  {showThinking && (
                    <div
                      ref={thinkingRef}
                      className="bg-white border border-indigo-100 rounded p-2 text-[10px] font-mono text-indigo-900 whitespace-pre-wrap max-h-32 overflow-y-auto italic"
                    >
                      {thinkingText}
                    </div>
                  )}
                </div>
              )}

              {/* Live preview */}
              <div
                ref={previewRef}
                className="bg-white border border-gray-200 rounded p-2 text-[11px] font-mono text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto"
              >
                {streamPreview || <span className="text-gray-400 italic">{thinkingText ? "Claude está razonando… el análisis aparecerá en cuanto termine." : "Esperando primera respuesta de Claude…"}</span>}
              </div>
              <div className="text-[10px] text-gray-500 flex items-center justify-between">
                <span>{streamPreview.length.toLocaleString("es-ES")} chars recibidos</span>
                <span className="text-purple-600">▸ streaming en vivo vía SSE</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
